import { connectedLabFragment } from 'apollo/fragments';
import { dentistFragment, linkedOrganizationFragment, nameFragment, technicianFragment } from 'apollo/fragments/user';
import { gql } from 'graphql.macro';

export const addLocalDentistTemplate = gql`
    mutation addLocalDentist(
        $clinic: MongoDbObjectId!
        $name: NameInput!
        $email: String
        $phone: String
        $note: String
        $localId: String
    ) {
        addLocalDentist(clinic: $clinic, name: $name, email: $email, phone: $phone, note: $note, localId: $localId) {
            _id
            name {
                firstName
                lastName
            }
            fullName
            email
            role
            local
            dentistLocalIds {
                lab
                clinic
                localId
            }
        }
    }
`;

export const assignInternalDentistIdTemplate = gql`
    mutation assignInternalDentistId($dentistId: MongoDbObjectId!, $clinicId: MongoDbObjectId!) {
        assignInternalDentistId(dentistId: $dentistId, clinicId: $clinicId) {
            _id
            dentistLocalIds {
                lab
                clinic
                localId
            }
        }
    }
`;

export const addWhiteLabelUserToClientTemplate = gql`
    mutation addWhiteLabelUserToClient(
        $clientId: MongoDbObjectId!
        $name: NameInput!
        $email: String!
        $phone: String
        $note: String
        $localId: String
    ) {
        addWhiteLabelUserToClient(
            clientId: $clientId
            name: $name
            email: $email
            phone: $phone
            note: $note
            localId: $localId
        ) {
            _id
            name {
                firstName
                lastName
            }
            fullName
            email
            role
            local
            dentistLocalIds {
                lab
                clinic
                localId
            }
        }
    }
`;

export const modifyLocalDentistTemplate = gql`
    mutation modifyLocalDentist(
        $_id: MongoDbObjectId!
        $clinic: MongoDbObjectId!
        $name: NameInput!
        $email: String
        $phone: String
        $note: String
        $localId: String
        $active: Boolean
    ) {
        modifyLocalDentist(
            _id: $_id
            clinic: $clinic
            name: $name
            email: $email
            phone: $phone
            note: $note
            localId: $localId
            active: $active
        ) {
            _id
            name {
                firstName
                lastName
            }
            fullName
            email
            phone
            active
            dentistNotes {
                lab
                clinic
                note
            }
            dentistLocalIds {
                lab
                clinic
                localId
            }
        }
    }
`;

export const setLabDentistDefaultClinicTemplate = gql`
    mutation setLabDentistDefaultClinic(
        $dentistId: MongoDbObjectId!
        $mainClinic: MongoDbObjectId!
        $defaultClinic: MongoDbObjectId
    ) {
        setLabDentistDefaultClinic(dentistId: $dentistId, mainClinic: $mainClinic, defaultClinic: $defaultClinic) {
            _id
            labDentistDefaultClinics {
                mainClinic
                defaultClinic {
                    _id
                    name
                }
            }
        }
    }
`;

export const addDentistTemplate = gql`
    mutation addDentist(
        $name: NameInput!
        $email: String!
        $role: String!
        $organization: MongoDbObjectId!
        $defaultClinic: MongoDbObjectId
        $title: String
        $authMethod: AuthMethod
        $syncid: String
        $personalIdentityNumber: String
        $active: Boolean
        $note: String
        $enableUserSync: Boolean
    ) {
        addDentist(
            name: $name
            email: $email
            role: $role
            organization: $organization
            defaultClinic: $defaultClinic
            title: $title
            authMethod: $authMethod
            syncid: $syncid
            personalIdentityNumber: $personalIdentityNumber
            active: $active
            note: $note
            enableUserSync: $enableUserSync
        ) {
            ...dentistFields
        }
    }
    ${dentistFragment}
`;

export const modifyDentistTemplate = gql`
    mutation modifyDentist($dentist: DentistInput!) {
        modifyDentist(dentist: $dentist) {
            ...dentistFields
        }
    }
    ${dentistFragment}
`;

export const addTechnicianTemplate = gql`
    mutation addTechnician(
        $name: NameInput!
        $email: String!
        $role: String!
        $organization: MongoDbObjectId!
        $isShared: Boolean
        $authMethod: AuthMethod
        $syncid: String
        $personalIdentityNumber: String
        $enableUserSync: Boolean
    ) {
        addTechnician(
            name: $name
            email: $email
            role: $role
            organization: $organization
            isShared: $isShared
            authMethod: $authMethod
            syncid: $syncid
            personalIdentityNumber: $personalIdentityNumber
            enableUserSync: $enableUserSync
        ) {
            ...technicianFields
        }
    }
    ${technicianFragment}
`;

export const modifyTechnicianTemplate = gql`
    mutation modifyTechnician($technician: TechnicianInput!) {
        modifyTechnician(technician: $technician) {
            ...technicianFields
            salesforceUser {
                id
                name
                email
            }
        }
    }
    ${technicianFragment}
`;

export const updateNotificationTemplate = gql`
    mutation ($notificationSettings: NotificationSettingsInput!) {
        updateNotificationSettings(notificationSettings: $notificationSettings) {
            emailNotifications {
                newMessage
                otherEmail
                type
                orderReceivedByLab
                orderDeclinedByLab
                orderSentByLab
            }
            generalNotifications {
                newMessage
                orderReceivedByLab
                orderDeclinedByLab
                orderSentByLab
                orderNotReceived {
                    active
                    days
                }
            }
        }
    }
`;

export const modifyConstructionSettingsTemplate = gql`
    mutation modifyConstructionSettings(
        $contactType: String
        $metalDesign: String
        $occlusionContact: String
        $ponticDesign: String
    ) {
        modifyConstructionSettings(
            contactType: $contactType
            metalDesign: $metalDesign
            occlusionContact: $occlusionContact
            ponticDesign: $ponticDesign
        ) {
            constructionSetting {
                contactType
                metalDesign
                occlusionContact
                ponticDesign
            }
        }
    }
`;

export const updateUserSystemSettingsTemplate = gql`
    mutation modifyUserSystemSettings($userSystemSettings: UserSystemSettingsInput!) {
        modifyUserSystemSettings(userSystemSettings: $userSystemSettings) {
            systemSetting {
                clinic {
                    _id
                    name
                }
                autoArchiveOrderDays
            }
            localeFormat {
                language
                date
                number
            }
            isShared
            viewSettings {
                orderModified
                deliveryDatePassed
            }
        }
    }
`;

export const modifyUserAccountSettingsTemplate = gql`
    mutation modifyUserAccountSettings($firstName: NameRegExp!, $lastName: NameRegExp!) {
        modifyUserAccountSettings(firstName: $firstName, lastName: $lastName) {
            name {
                firstName
                lastName
            }
        }
    }
`;

export const linkUserToOrganizationTemplate = gql`
    mutation linkUserToOrganization($userId: MongoDbObjectId!, $linkDetails: LinkUserInput!) {
        linkUserToOrganization(userId: $userId, linkDetails: $linkDetails) {
            _id
            fullName
            name {
                ...nameFields
            }
            email
            authMethod
            syncid
            title
            phone
            comments
            role
            organization {
                _id
                name
                type
                isWhiteLabeled
                connectedLabs {
                    ...connectedLabFields
                }
            }
            active
            systemSetting {
                clinic {
                    _id
                    name
                }
            }
            local
            linkedOrganizations {
                ...linkedOrganizationFields
            }
            whiteLabelAccess
        }
    }

    ${nameFragment}
    ${linkedOrganizationFragment}
    ${connectedLabFragment}
`;

export const requestOwnPasswordResetTemplate = gql`
    mutation requestOwnPasswordReset {
        requestOwnPasswordReset
    }
`;

export const requestEnableIduraEidTemplate = gql`
    mutation requestEnableIduraEid($method: IduraLoginMethod!, $personalIdentityNumber: String) {
        requestEnableIduraEid(method: $method, personalIdentityNumber: $personalIdentityNumber)
    }
`;

export const requestEnableTotpLoginTemplate = gql`
    mutation {
        requestEnableTotpLogin
    }
`;

export const enableTotpLoginTemplate = gql`
    mutation enableTotpLogin($code: String!) {
        enableTotpLogin(code: $code)
    }
`;
export const disableTotpLoginTemplate = gql`
    mutation {
        disableTotpLogin
    }
`;

export const whiteLabelDentistAccessTemplate = gql`
    mutation whiteLabelDentistAccess($dentistId: MongoDbObjectId!) {
        whiteLabelDentistAccess(dentistId: $dentistId) {
            _id
            whiteLabelAccess
        }
    }
`;

export const convertLocalDentistToConnectDentistTemplate = gql`
    mutation convertLocalDentistToConnectedDentist(
        $_id: MongoDbObjectId!
        $clinic: MongoDbObjectId!
        $name: NameInput!
        $email: String
        $phone: String
        $note: String
        $localId: String
        $active: Boolean
    ) {
        convertLocalDentistToConnectedDentist(
            _id: $_id
            clinic: $clinic
            name: $name
            email: $email
            phone: $phone
            note: $note
            localId: $localId
            active: $active
        ) {
            _id
            name {
                firstName
                lastName
            }
            fullName
            email
            phone
            active
            local
            dentistNotes {
                lab
                clinic
                note
            }
            dentistLocalIds {
                lab
                clinic
                localId
            }
        }
    }
`;

export const switchOrganizationTemplate = gql`
    mutation switchOrganization($organizationId: MongoDbObjectId!, $wlLabId: MongoDbObjectId) {
        switchOrganization(organizationId: $organizationId, wlLabId: $wlLabId) {
            token
        }
    }
`;

export const setOrderListSelectedProfileTemplate = gql`
    mutation setOrderListSelectedProfile($scope: OrderListScope!, $profileId: String) {
        setOrderListSelectedProfile(scope: $scope, profileId: $profileId) {
            selectedMainProfileId
            selectedWorkflowProfileId
            hasSelectedMainProfile
            hasSelectedWorkflowProfile
            columnWidths {
                profileId
                widths {
                    columnKey
                    width
                }
            }
        }
    }
`;

export const setOrderListColumnWidthsTemplate = gql`
    mutation setOrderListColumnWidths($profileId: MongoDbObjectId!, $widths: [OrderListColumnWidthInput!]!) {
        setOrderListColumnWidths(profileId: $profileId, widths: $widths) {
            selectedMainProfileId
            selectedWorkflowProfileId
            hasSelectedMainProfile
            hasSelectedWorkflowProfile
            columnWidths {
                profileId
                widths {
                    columnKey
                    width
                }
            }
        }
    }
`;

export const requestDentistListExportTemplate = gql`
    mutation requestDentistListExport($clinicId: MongoDbObjectId) {
        requestDentistListExport(clinicId: $clinicId)
    }
`;

export const saveLabDentistNoteTemplate = gql`
    mutation saveLabDentistNote($dentistId: MongoDbObjectId!, $clinicId: MongoDbObjectId!, $note: String!) {
        saveLabDentistNote(dentistId: $dentistId, clinicId: $clinicId, note: $note) {
            _id
            dentistNotes {
                lab
                clinic
                note
            }
        }
    }
`;
